import type { ReactNode } from 'react'
import cx from 'classnames'
import { type AvatarSubject, Avatar } from '@/components/Avatar'
import { Icon, type IconName } from '@/components/Icon'
import {
  computeDeadlineDisplay,
  type DeadlineProximity,
} from '@/features/jobs/deadline'
import { isEmptyNode } from '@/utils/react-util'
import styles from './OpportunityListItem.module.css'
import { formattedTimeUntil, dateFormatters } from '@/utils/date-util'
import {
  OpportunityItem,
  OpportunityItemAvatarInfo,
  OpportunityItemAvatar,
  OpportunityItemInfo,
  OpportunityItemTitle,
  OpportunityItemSubtitle,
  ForwardIcon,
} from '@/features/opportunities/components/OpportunityItem'
import logoWorklayerBlueUrl from '@/assets/misc/logo_worklayer-blue.svg'

export interface OpportunityListItemProps {
  name: string
  description: string
  client?: AvatarSubject
  workspaceName: string
  workspaceIcon: string
  showWorkspaceInfo: boolean
  isDisabled: boolean
  selected: boolean
  onSelect: () => unknown
  /**
   * Seconds until a dispatch selection completes; `null` when not dispatching.
   */
  selectionCountdown: number | null
  deadline: Date | null
  providerCutAmount: string | null
  testId?: string
}

export function OpportunityListItem({
  name,
  description,
  client,
  workspaceName,
  workspaceIcon,
  showWorkspaceInfo,
  isDisabled,
  selected,
  onSelect,
  selectionCountdown,
  deadline,
  providerCutAmount,
  testId,
}: OpportunityListItemProps) {
  return (
    <OpportunityItem
      isDisabled={isDisabled}
      selected={selected}
      onSelect={onSelect}
      testId={testId}
    >
      <OpportunityItemAvatarInfo>
        <OpportunityItemAvatar
          isDisabled={isDisabled}
          avatar={<Avatar subject={client} />}
          secondaryAvatar={
            showWorkspaceInfo && (
              <Avatar
                size={16}
                subject={{
                  avatar: workspaceIcon,
                  avatarFallback: logoWorklayerBlueUrl,
                }}
              />
            )
          }
        />
        <OpportunityItemInfo>
          <OpportunityItemTitle
            isDisabled={isDisabled}
            title={name}
            secondaryTitle={showWorkspaceInfo ? workspaceName : undefined}
          />
          <OpportunityItemSubtitle>
            {description || 'No description'}
          </OpportunityItemSubtitle>
          <DispatchCountdown selectionCountdown={selectionCountdown} />
        </OpportunityItemInfo>
      </OpportunityItemAvatarInfo>
      <Columns>
        <DeadlineOrSelectionTimeLabel
          selectionCountdown={selectionCountdown}
          deadline={deadline}
        />
        <ProCutOrSelectionTimeRemaining
          selectionCountdown={selectionCountdown}
          providerCutAmount={providerCutAmount}
        />
      </Columns>
      <ForwardIcon isDisabled={isDisabled} />
    </OpportunityItem>
  )
}

interface DispatchCountdownProps {
  selectionCountdown: number | null
}

function DispatchCountdown({ selectionCountdown }: DispatchCountdownProps) {
  if (!selectionCountdown) {
    return null
  }
  return (
    <div className={styles.smallScreenSelectionCountdown}>
      <Icon className={styles.timeUntilSelectionIcon} name="clock" auto />
      Time until selection&nbsp;&nbsp;
      <span className={styles.countdown}>
        {formattedTimeUntil(selectionCountdown)}
      </span>
    </div>
  )
}

interface DateDeadlineProps {
  deadline: Date | null
}

/**
 * Component for showing the job's deadline.
 */
function DateDeadline({ deadline }: DateDeadlineProps) {
  const deadlineDisplay = computeDeadlineDisplay({
    status: 'Unclaimed',
    dateDeadline: deadline,
  })

  return deadline ? (
    <div className={styles.deadlineOrSelectionTimeLabel}>
      <Column
        description="Due date"
        icon={
          <ColumnIcon
            name="clock"
            variant={deadlineProximityToIconVariant(deadlineDisplay.proximity)}
          />
        }
      >
        <span>{dateFormatters.dateLong.format(deadline)}</span>
        <br />
        <span>at {dateFormatters.timeDefault.format(deadline)}</span>
      </Column>
    </div>
  ) : null
}

interface DeadlineOrSelectionTimeLabelProps {
  selectionCountdown: number | null
  deadline: Date | null
}

/**
 * A component that displays either the deadline or the time selection label,
 * depending on whether a provider selection is ongoing.
 */
function DeadlineOrSelectionTimeLabel({
  selectionCountdown,
  deadline,
}: DeadlineOrSelectionTimeLabelProps) {
  return selectionCountdown ? (
    <div className={styles.deadlineOrSelectionTimeLabel}>
      <div className={styles.timeUntilSelectionCell}>
        <Icon className={styles.timeUntilSelectionIcon} name="clock" auto />
        Time until selection
      </div>
    </div>
  ) : (
    <DateDeadline deadline={deadline} />
  )
}

interface ProCutOrSelectionTimeRemainingProps {
  selectionCountdown: number | null
  providerCutAmount: string | null
}

/**
 * Component that shows either the pro cut or the selection time remaining
 * depending on whether a provider is being selected for the job.
 */
function ProCutOrSelectionTimeRemaining({
  selectionCountdown,
  providerCutAmount,
}: ProCutOrSelectionTimeRemainingProps) {
  return (
    <div className={styles.proCutOrSelectionTimeRemaining}>
      {selectionCountdown ? (
        <span className={styles.countdown}>
          {formattedTimeUntil(selectionCountdown)}
        </span>
      ) : (
        providerCutAmount !== null && (
          <Column description="Total" hideOnSmallerScreens>
            {providerCutAmount}
          </Column>
        )
      )}
    </div>
  )
}

interface ColumnsProps {
  children?: ReactNode
}

function Columns({ children }: ColumnsProps) {
  if (isEmptyNode(children)) {
    return null
  }
  return <div className={styles.columns}>{children}</div>
}

type IconVariant = 'neutral' | 'info' | 'success' | 'warning' | 'danger'

interface ColumnProps {
  children?: ReactNode
  description?: ReactNode
  icon?: ReactNode
  variant?: IconVariant
  tooltip?: string
  hideOnSmallerScreens?: boolean
}

function Column({
  tooltip,
  children,
  icon,
  description,
  hideOnSmallerScreens,
  variant = 'neutral',
}: ColumnProps) {
  return (
    <div
      className={cx(
        styles.column,
        styles[variant],
        hideOnSmallerScreens && styles.hideOnSmallerScreens,
      )}
      title={tooltip}
    >
      <div className={styles.columnTitle}>{children}</div>
      <div className={styles.columnContent}>
        {icon}
        <div className={styles.columnDescription}>{description}</div>
      </div>
    </div>
  )
}

interface ColumnIconProps {
  name: IconName
  variant?: IconVariant
}

function ColumnIcon({ name, variant = 'neutral' }: ColumnIconProps) {
  return (
    <span className={cx(styles.columnIcon, styles[variant])}>
      <Icon name={name} auto />
    </span>
  )
}

function deadlineProximityToIconVariant(
  proximity: DeadlineProximity,
): IconVariant {
  switch (proximity) {
    case 'PrettyFar':
      return 'success'
    case 'Far':
      return 'warning'
    case 'Close':
      return 'danger'
    case 'NotApplicable':
      return 'neutral'
  }
}
